/**
 * Step 4: Review & Edit
 *
 * Single mode: Form to review/edit one candidate.
 * Bulk mode: Table view of all parsed candidates with retry/skip/edit.
 */

import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  Edit,
  Refresh,
  CheckCircle,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import TagInput from "../TagInput";
import { recruitingAPI } from "../../services/recruitingApi";
import type {
  WizardState,
  BulkFileResult,
  ParsedCandidate,
} from "./CandidateImportWizard";

interface Props {
  wizardState: WizardState;
  onNext: () => void;
  onBack: () => void;
  updateState: (updates: Partial<WizardState>) => void;
}

// Inline edit dialog for a single candidate in bulk mode
const CandidateEditDialog: React.FC<{
  candidate: ParsedCandidate;
  open: boolean;
  onClose: () => void;
  onSave: (data: ParsedCandidate) => void;
}> = ({ candidate, open, onClose, onSave }) => {
  const { t } = useTranslation("recruiting");
  const [data, setData] = useState<any>({ ...candidate });

  const handleChange = (field: string, value: any) => {
    setData((prev: any) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("importWizard.step4.editCandidate")}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <TextField
              label={t("importWizard.step4.firstName")}
              value={data.first_name || ""}
              onChange={(e) => handleChange("first_name", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t("importWizard.step4.lastName")}
              value={data.last_name || ""}
              onChange={(e) => handleChange("last_name", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t("importWizard.step4.email")}
              value={data.email || ""}
              onChange={(e) => handleChange("email", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t("importWizard.step4.phone")}
              value={data.phone || ""}
              onChange={(e) => handleChange("phone", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t("importWizard.step4.location")}
              value={data.location || ""}
              onChange={(e) => handleChange("location", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t("importWizard.step4.mainSkill")}
              value={data.main_skill || ""}
              onChange={(e) => handleChange("main_skill", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              label={t("importWizard.step4.seniority")}
              value={data.seniority || "mid"}
              onChange={(e) => handleChange("seniority", e.target.value)}
              fullWidth
            >
              <MenuItem value="junior">
                {t("importWizard.step4.junior")}
              </MenuItem>
              <MenuItem value="mid">{t("importWizard.step4.mid")}</MenuItem>
              <MenuItem value="senior">
                {t("importWizard.step4.senior")}
              </MenuItem>
              <MenuItem value="lead">{t("importWizard.step4.lead")}</MenuItem>
              <MenuItem value="principal">
                {t("importWizard.step4.principal")}
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              label={t("importWizard.step4.englishLevel")}
              value={data.english_level || "intermediate"}
              onChange={(e) => handleChange("english_level", e.target.value)}
              fullWidth
            >
              <MenuItem value="basic">{t("importWizard.step4.basic")}</MenuItem>
              <MenuItem value="intermediate">
                {t("importWizard.step4.intermediate")}
              </MenuItem>
              <MenuItem value="advanced">
                {t("importWizard.step4.advanced")}
              </MenuItem>
              <MenuItem value="native">
                {t("importWizard.step4.native")}
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("importWizard.step4.summary")}
              value={data.summary || ""}
              onChange={(e) => handleChange("summary", e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TagInput
              value={data.tags || []}
              onChange={(tags) => handleChange("tags", tags)}
              placeholder={t("importWizard.step4.addTags")}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("importWizard.step4.cancel")}</Button>
        <Button
          variant="contained"
          onClick={() => onSave(data)}
          disabled={!data.first_name || !data.last_name}
        >
          {t("importWizard.step4.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Step4_Review: React.FC<Props> = ({
  wizardState,
  onNext,
  onBack,
  updateState,
}) => {
  const { t } = useTranslation("recruiting");
  const isBulk = wizardState.importMethod === "bulk-cv";

  // Single mode state
  const [candidateData, setCandidateData] = useState<Partial<ParsedCandidate>>(
    wizardState.parsedCandidate || {},
  );

  // Bulk mode state
  const [bulkResults, setBulkResults] = useState<BulkFileResult[]>(
    wizardState.bulkResults || [],
  );
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [retryingIndex, setRetryingIndex] = useState<number | null>(null);

  // Single mode handlers
  const handleFieldChange = (field: string, value: any) => {
    setCandidateData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSingleNext = () => {
    updateState({ parsedCandidate: candidateData as ParsedCandidate });
    onNext();
  };

  // Bulk mode handlers
  const handleToggleInclude = (index: number) => {
    setBulkResults((prev) =>
      prev.map((r) =>
        r.index === index ? { ...r, included: !r.included } : r,
      ),
    );
  };

  const handleSelectAll = () => {
    const allSuccessIncluded = bulkResults
      .filter((r) => r.status === "success")
      .every((r) => r.included);

    setBulkResults((prev) =>
      prev.map((r) =>
        r.status === "success" ? { ...r, included: !allSuccessIncluded } : r,
      ),
    );
  };

  const handleEditSave = (data: ParsedCandidate) => {
    setBulkResults((prev) =>
      prev.map((r) =>
        r.index === editingIndex ? { ...r, parsedCandidate: data } : r,
      ),
    );
    setEditingIndex(null);
  };

  // Retry mutation using React Query
  const retryMutation = useMutation(
    (formData: FormData) => recruitingAPI.uploadSingleCV(formData),
    {
      onSuccess: (response) => {
        const index = retryingIndex!;
        if (response.success) {
          setBulkResults((prev) =>
            prev.map((r) =>
              r.index === index
                ? {
                    ...r,
                    status: "success" as const,
                    parsedCandidate: response.data,
                    fileId: response.data?._fileId,
                    error: undefined,
                    included: true,
                  }
                : r,
            ),
          );
        } else {
          setBulkResults((prev) =>
            prev.map((r) =>
              r.index === index
                ? {
                    ...r,
                    error: response.error || "Retry failed",
                  }
                : r,
            ),
          );
        }
        setRetryingIndex(null);
      },
      onError: (error: any) => {
        const index = retryingIndex!;
        setBulkResults((prev) =>
          prev.map((r) =>
            r.index === index
              ? {
                  ...r,
                  error:
                    error.response?.data?.error ||
                    error.message ||
                    "Retry failed",
                }
              : r,
          ),
        );
        setRetryingIndex(null);
      },
    },
  );

  const handleRetry = (index: number) => {
    const file = wizardState.uploadedFiles?.[index];
    if (!file) return;

    setRetryingIndex(index);
    const formData = new FormData();
    formData.append("cv", file);
    retryMutation.mutate(formData);
  };

  const handleBulkNext = () => {
    updateState({ bulkResults: bulkResults });
    onNext();
  };

  // Bulk mode render
  if (isBulk) {
    const includedCount = bulkResults.filter((r) => r.included).length;
    const successCount = bulkResults.filter(
      (r) => r.status === "success",
    ).length;
    const errorCount = bulkResults.filter((r) => r.status === "error").length;
    const editingResult =
      editingIndex !== null
        ? bulkResults.find((r) => r.index === editingIndex)
        : null;

    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          {t("importWizard.step4.reviewCandidatesTitle")}
        </Typography>

        <Alert severity={errorCount === 0 ? "success" : "info"} sx={{ mb: 3 }}>
          {t("importWizard.step4.selectedForImport", {
            selected: includedCount,
            total: bulkResults.length,
          })}
          {errorCount > 0 &&
            ` ${t("importWizard.step4.failedRetrySkip", { count: errorCount })}`}
        </Alert>

        <TableContainer sx={{ maxHeight: 400, mb: 3 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      successCount > 0 &&
                      bulkResults
                        .filter((r) => r.status === "success")
                        .every((r) => r.included)
                    }
                    indeterminate={
                      bulkResults
                        .filter((r) => r.status === "success")
                        .some((r) => r.included) &&
                      !bulkResults
                        .filter((r) => r.status === "success")
                        .every((r) => r.included)
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>{t("importWizard.step4.colStatus")}</TableCell>
                <TableCell>{t("importWizard.step4.colFile")}</TableCell>
                <TableCell>{t("importWizard.step4.colName")}</TableCell>
                <TableCell>{t("importWizard.step4.colEmail")}</TableCell>
                <TableCell>{t("importWizard.step4.colMainSkill")}</TableCell>
                <TableCell align="right">
                  {t("importWizard.step4.colActions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bulkResults.map((result) => (
                <TableRow
                  key={result.index}
                  sx={{
                    bgcolor:
                      result.status === "error" ? "error.light" : undefined,
                    opacity:
                      result.status === "error" && !result.included ? 0.7 : 1,
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={result.included}
                      disabled={result.status === "error"}
                      onChange={() => handleToggleInclude(result.index)}
                    />
                  </TableCell>
                  <TableCell>
                    {result.status === "success" && (
                      <CheckCircle color="success" fontSize="small" />
                    )}
                    {result.status === "error" && (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 150,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {result.fileName}
                  </TableCell>
                  <TableCell>
                    {result.status === "success" ? (
                      `${result.parsedCandidate?.first_name} ${result.parsedCandidate?.last_name}`
                    ) : (
                      <Chip
                        label={t("importWizard.step4.failed")}
                        color="error"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {result.parsedCandidate?.email ||
                      (result.status === "error" ? result.error : "-")}
                  </TableCell>
                  <TableCell>
                    {result.parsedCandidate?.main_skill || "-"}
                  </TableCell>
                  <TableCell align="right">
                    {result.status === "success" && (
                      <IconButton
                        size="small"
                        onClick={() => setEditingIndex(result.index)}
                        title={t("importWizard.step4.editCandidate")}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    )}
                    {result.status === "error" && (
                      <IconButton
                        size="small"
                        onClick={() => handleRetry(result.index)}
                        disabled={retryingIndex === result.index}
                        title="Retry"
                      >
                        {retryingIndex === result.index ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Refresh fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {editingResult?.parsedCandidate && (
          <CandidateEditDialog
            candidate={editingResult.parsedCandidate}
            open={editingIndex !== null}
            onClose={() => setEditingIndex(null)}
            onSave={handleEditSave}
          />
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button onClick={onBack} variant="outlined">
            {t("importWizard.step4.back")}
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkNext}
            disabled={includedCount === 0}
            data-testid="next-button"
          >
            {t("importWizard.step4.nextConfirm", { count: includedCount })}
          </Button>
        </Box>
      </Box>
    );
  }

  // Single mode render (existing behavior)
  const isManualEntry =
    wizardState.parseError !== null && wizardState.parseError !== undefined;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t("importWizard.step4.reviewTitle")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {isManualEntry
          ? t("importWizard.step4.manualEntry")
          : t("importWizard.step4.reviewExtracted")}
      </Typography>

      {isManualEntry && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("importWizard.step4.extractionFailedAlert")}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t("importWizard.step4.firstName")}
            name="first_name"
            value={candidateData.first_name || ""}
            onChange={(e) => handleFieldChange("first_name", e.target.value)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t("importWizard.step4.lastName")}
            name="last_name"
            value={candidateData.last_name || ""}
            onChange={(e) => handleFieldChange("last_name", e.target.value)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t("importWizard.step4.emailRequired")}
            name="email"
            type="email"
            value={candidateData.email || ""}
            onChange={(e) => handleFieldChange("email", e.target.value)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t("importWizard.step4.phone")}
            name="phone"
            value={candidateData.phone || ""}
            onChange={(e) => handleFieldChange("phone", e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t("importWizard.step4.location")}
            name="location"
            value={candidateData.location || ""}
            onChange={(e) => handleFieldChange("location", e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t("importWizard.step4.linkedinUrl")}
            name="linkedin_url"
            value={candidateData.linkedin_url || ""}
            onChange={(e) => handleFieldChange("linkedin_url", e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t("importWizard.step4.mainSkill")}
            name="main_skill"
            value={candidateData.main_skill || ""}
            onChange={(e) => handleFieldChange("main_skill", e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            label={t("importWizard.step4.seniority")}
            name="seniority"
            value={candidateData.seniority || "mid"}
            onChange={(e) => handleFieldChange("seniority", e.target.value)}
            fullWidth
          >
            <MenuItem value="junior">{t("importWizard.step4.junior")}</MenuItem>
            <MenuItem value="mid">{t("importWizard.step4.mid")}</MenuItem>
            <MenuItem value="senior">{t("importWizard.step4.senior")}</MenuItem>
            <MenuItem value="lead">{t("importWizard.step4.lead")}</MenuItem>
            <MenuItem value="principal">
              {t("importWizard.step4.principal")}
            </MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t("importWizard.step4.roleType")}
            name="role_type"
            value={candidateData.role_type || ""}
            onChange={(e) => handleFieldChange("role_type", e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t("importWizard.step4.expectedDailyRate")}
            name="expected_rate"
            type="number"
            value={candidateData.expected_rate || ""}
            onChange={(e) =>
              handleFieldChange(
                "expected_rate",
                parseFloat(e.target.value) || null,
              )
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t("importWizard.step4.noticePeriod")}
            name="notice_period"
            value={candidateData.notice_period || ""}
            onChange={(e) => handleFieldChange("notice_period", e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            label={t("importWizard.step4.englishLevel")}
            name="english_level"
            value={candidateData.english_level || "intermediate"}
            onChange={(e) => handleFieldChange("english_level", e.target.value)}
            fullWidth
          >
            <MenuItem value="basic">{t("importWizard.step4.basic")}</MenuItem>
            <MenuItem value="intermediate">
              {t("importWizard.step4.intermediate")}
            </MenuItem>
            <MenuItem value="advanced">
              {t("importWizard.step4.advanced")}
            </MenuItem>
            <MenuItem value="native">{t("importWizard.step4.native")}</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t("importWizard.step4.summary")}
            name="summary"
            value={candidateData.summary || ""}
            onChange={(e) => handleFieldChange("summary", e.target.value)}
            multiline
            rows={4}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TagInput
            value={candidateData.tags || []}
            onChange={(tags) => handleFieldChange("tags", tags)}
            placeholder={t("importWizard.step4.addTags")}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
        <Button onClick={onBack} variant="outlined">
          {t("importWizard.step4.back")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSingleNext}
          disabled={
            !candidateData.first_name ||
            !candidateData.last_name ||
            !candidateData.email
          }
          data-testid="next-button"
        >
          {t("importWizard.step4.nextConfirmSingle")}
        </Button>
      </Box>
    </Box>
  );
};

export default Step4_Review;
